import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, CircularProgress, Alert,
  Button, TextField, InputAdornment, Tooltip, IconButton,
  Collapse, LinearProgress, Select, MenuItem, FormControl, InputLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  InfoOutlined as InfoIcon,
  WarningAmber as WarningIcon,
  ShoppingCart as OrderIcon,
} from '@mui/icons-material';

const API = '/api/forecast';
const PURCHASE_API = '/api/purchase';
const APP_API_KEY = process.env.REACT_APP_API_KEY || 'e4ccbc0bd680211f4e4553ea3ab07d8a55707e79cde61faa10c51c55c6da35a9';

const ABC_COLORS = {
  A: { bg: '#e8f5e9', border: '#4caf50', text: '#2e7d32', label: 'A' },
  B: { bg: '#fff8e1', border: '#ff9800', text: '#e65100', label: 'B' },
  C: { bg: '#e3f2fd', border: '#2196f3', text: '#1565c0', label: 'C' },
  D: { bg: '#fafafa', border: '#9e9e9e', text: '#616161', label: 'D' },
};

const MONTH_NAMES = ['Jan','Feb','Mrt','Apr','Mei','Jun','Jul','Aug','Sep','Okt','Nov','Dec'];

function MiniBarChart({ data2024, data2025, currentMonth }) {
  const maxVal = Math.max(...data2024, ...data2025, 1);
  return (
    <Box sx={{ display: 'flex', gap: '2px', alignItems: 'flex-end', height: 40 }}>
      {data2024.map((v24, i) => {
        const v25 = data2025[i];
        const isPast = i < currentMonth;
        return (
          <Box key={i} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px' }}>
            <Box sx={{ display: 'flex', gap: '1px', alignItems: 'flex-end' }}>
              <Box sx={{
                width: 4, height: Math.max(2, (v24 / maxVal) * 32),
                backgroundColor: '#bdbdbd', borderRadius: '1px 1px 0 0'
              }} />
              <Box sx={{
                width: 4, height: Math.max(2, (v25 / maxVal) * 32),
                backgroundColor: isPast ? '#ffcc00' : '#4caf50',
                borderRadius: '1px 1px 0 0',
                opacity: isPast ? 1 : 0.6
              }} />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

function ProductRow({ product, expanded, onToggle }) {
  const abc = ABC_COLORS[product.abcClass];
  const currentMonth = new Date().getMonth();

  const growthIcon = product.growthRate > 10
    ? <TrendingUpIcon sx={{ fontSize: 14, color: 'success.main' }} />
    : product.growthRate < -10
    ? <TrendingDownIcon sx={{ fontSize: 14, color: 'error.main' }} />
    : <TrendingFlatIcon sx={{ fontSize: 14, color: 'text.secondary' }} />;

  return (
    <>
      <TableRow
        hover
        onClick={onToggle}
        sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
      >
        <TableCell sx={{ py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={product.abcClass}
              size="small"
              sx={{
                backgroundColor: abc.bg, color: abc.text,
                border: `1px solid ${abc.border}`, fontWeight: 'bold',
                minWidth: 24, height: 20, fontSize: 11
              }}
            />
            <Box>
              <Typography variant="body2" fontWeight="medium" sx={{ lineHeight: 1.2 }}>
                {product.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">{product.sku}</Typography>
            </Box>
          </Box>
        </TableCell>

        <TableCell align="center" sx={{ py: 1 }}>
          <MiniBarChart
            data2024={product.monthProfile.map(m => m.qty2024)}
            data2025={product.monthProfile.map(m => m.qty2025)}
            currentMonth={currentMonth}
          />
        </TableCell>

        {/* Verkopen + groei */}
        <TableCell align="center" sx={{ py: 1 }}>
          <Typography variant="body2">{product.totalSold}</Typography>
        </TableCell>

        <TableCell align="center" sx={{ py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
            {growthIcon}
            <Typography variant="body2" color={product.growthRate > 10 ? 'success.main' : product.growthRate < -10 ? 'error.main' : 'text.secondary'}>
              {product.growthRate > 0 ? '+' : ''}{product.growthRate}%
            </Typography>
          </Box>
        </TableCell>

        {/* Huidige situatie — voorraad + leadtime + status */}
        <TableCell sx={{ py: 1, minWidth: 160, backgroundColor: '#fafafa', borderLeft: '2px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {/* Voorraad */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">Voorraad nu:</Typography>
              <Typography variant="body2" fontWeight="bold"
                color={product.currentStock < product.reorderPoint ? 'error.main' : 'success.main'}>
                {product.currentStock ?? 0} st.
              </Typography>
            </Box>
            {/* Onderweg */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">Onderweg:</Typography>
              <Typography variant="body2" color={product.openOrdersQty > 0 ? 'info.main' : 'text.disabled'}>
                {product.openOrdersQty > 0 ? `+${product.openOrdersQty} st.` : '—'}
              </Typography>
            </Box>
            {/* Levertijd */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">Levertijd:</Typography>
              <Typography variant="body2">{product.leadtimeWeeks} weken</Typography>
            </Box>
            {/* Status */}
            <Box sx={{ mt: 0.5 }}>
              {product.currentStock < product.reorderPoint ? (
                <Chip label="⚠ Onder bestelniveau" size="small" color="warning"
                  sx={{ fontSize: 10, height: 18, width: '100%' }} />
              ) : (
                <Chip label="✓ Voldoende voorraad" size="small" color="success"
                  sx={{ fontSize: 10, height: 18, width: '100%' }} />
              )}
            </Box>
          </Box>
        </TableCell>

        {/* Forecast maanden — netto besteladvies */}
        {product.forecast.map((f, i) => (
          <TableCell key={i} align="center" sx={{
            py: 0.5, px: 1,
            backgroundColor: !f.isSeason ? '#fafafa' : f.netOrder > 0 ? '#fff8e1' : '#f1f8e9',
            borderLeft: f.urgent ? '3px solid #f44336' : '1px solid #e0e0e0'
          }}>
            {f.isSeason ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3 }}>
                {/* Netto bestellen */}
                {f.netOrder > 0 ? (
                  <Tooltip title={`Bestel vóór ${f.orderByDate} | Verwachte verkoop: ${f.forecastQty} st. | Voorraad dan: ${f.stockAtMonth} st.`}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" fontWeight="bold"
                        color={f.urgent ? 'error.main' : 'warning.dark'} sx={{ lineHeight: 1 }}>
                        {f.netOrder} st.
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: 9, color: f.urgent ? 'error.main' : 'text.secondary' }}>
                        {f.urgent ? '⚠ BESTEL NU' : `vóór ${f.orderByDate.slice(5)}`}
                      </Typography>
                    </Box>
                  </Tooltip>
                ) : (
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="success.main" fontWeight="bold">✓</Typography>
                    <Typography variant="caption" sx={{ fontSize: 9, color: 'text.disabled' }}>
                      {f.stockAtMonth} st. over
                    </Typography>
                  </Box>
                )}
                {/* Prognose verkoop klein */}
                <Typography variant="caption" sx={{ fontSize: 9, color: 'text.disabled' }}>
                  ~{f.forecastQty} verkoop
                </Typography>
              </Box>
            ) : (
              <Typography variant="caption" color="text.disabled">—</Typography>
            )}
          </TableCell>
        ))}

        <TableCell align="center" sx={{ py: 1 }}>
          <IconButton size="small">
            {expanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={9 + (product.forecast?.length || 0)} sx={{ py: 0, border: 0 }}>
          <Collapse in={expanded}>
            <Box sx={{ py: 2, px: 2, backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0' }}>
              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {/* Maandprofiel tabel */}
                <Box sx={{ flex: 1, minWidth: 400 }}>
                  <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                    Maandprofiel 2024 vs 2025
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0 }}>
                    {product.monthProfile.map((m, i) => {
                      const max = Math.max(...product.monthProfile.map(x => Math.max(x.qty2024, x.qty2025)), 1);
                      const isSeason = [2,3,4,5,6,7,8].includes(i);
                      return (
                        <Box key={i} sx={{
                          flex: 1, textAlign: 'center', px: 0.5,
                          backgroundColor: isSeason ? '#f0f7ff' : 'transparent',
                          borderRadius: 1
                        }}>
                          <Typography variant="caption" sx={{ fontSize: 9, display: 'block', color: 'text.secondary' }}>
                            {m.month}
                          </Typography>
                          <Box sx={{ height: 40, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', gap: '2px' }}>
                            <Box sx={{ width: '80%', height: Math.max(1, (m.qty2025 / max) * 36), backgroundColor: '#ffcc00', borderRadius: '2px 2px 0 0' }} />
                            <Box sx={{ width: '80%', height: Math.max(1, (m.qty2024 / max) * 36), backgroundColor: '#bdbdbd', borderRadius: '2px 2px 0 0' }} />
                          </Box>
                          <Typography variant="caption" sx={{ fontSize: 9, display: 'block', color: '#ffcc00', fontWeight: 'bold' }}>
                            {m.qty2025 || ''}
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: 9, display: 'block', color: '#9e9e9e' }}>
                            {m.qty2024 || ''}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 12, height: 8, backgroundColor: '#ffcc00', borderRadius: 1 }} />
                      <Typography variant="caption">2025</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 12, height: 8, backgroundColor: '#bdbdbd', borderRadius: 1 }} />
                      <Typography variant="caption">2024</Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Stats */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 200 }}>
                  <Typography variant="caption" fontWeight="bold">Statistieken</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                    {[
                      ['Gem/mnd seizoen', `${product.avgSeasonMonth} st.`],
                      ['Piekmaand', `${product.peakQty} st.`],
                      ['Huidige voorraad (totaal)', `${product.currentStock ?? 0} st.`],
                      ['Onderweg (open POs)', `+${product.openOrdersQty || 0} st.`],
                      ['IJzeren voorraad', `${product.safetyStock} st.`],
                      ['Bestelniveau', `${product.reorderPoint} st.`],
                      ['Groei 24→25', `${product.growthRate > 0 ? '+' : ''}${product.growthRate}%`],
                    ].map(([label, value]) => (
                      <Box key={label} sx={{ backgroundColor: 'white', p: 1, borderRadius: 1, border: '1px solid #e0e0e0' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 10 }}>{label}</Typography>
                        <Typography variant="body2" fontWeight="bold">{value}</Typography>
                      </Box>
                    ))}
                  </Box>

                  <Box sx={{ mt: 1, p: 1, backgroundColor: abc.bg, borderRadius: 1, border: `1px solid ${abc.border}` }}>
                    <Typography variant="caption" sx={{ color: abc.text, fontWeight: 'bold', fontSize: 10 }}>
                      {product.abcClass}-artikel: {product.abcLabel}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function Prognose() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedSku, setExpandedSku] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [filterAbc, setFilterAbc] = useState('all');
  const [lastLoaded, setLastLoaded] = useState(null);

  const [suppliers, setSuppliers] = useState([]);
  const [orderDialog, setOrderDialog] = useState(null); // { supplier, items }
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(null);

  // Laad leveranciers
  useEffect(() => {
    fetch(`${PURCHASE_API}/suppliers`, { headers: { 'x-api-key': APP_API_KEY } })
      .then(r => r.json()).then(d => setSuppliers(Array.isArray(d) ? d : []));
  }, []);

  const supplierMap = useMemo(() => {
    const m = {};
    suppliers.forEach(s => { m[s.id] = s; });
    return m;
  }, [suppliers]);

  // Groepeer producten per leverancier voor besteladvies
  const productsBySupplier = useMemo(() => {
    if (!data?.products) return {};
    const grouped = {};
    data.products.forEach(p => {
      if (!p.supplier_id) return;
      const hasOrder = p.forecast?.some(f => f.netOrder > 0);
      if (!hasOrder) return;
      if (!grouped[p.supplier_id]) grouped[p.supplier_id] = [];
      grouped[p.supplier_id].push(p);
    });
    return grouped;
  }, [data]);

  const openOrderDialog = (supplierId) => {
    const supplier = supplierMap[supplierId];
    const products = productsBySupplier[supplierId] || [];
    // Maak een bestelrij per product per maand dat netto > 0
    const items = [];
    products.forEach(p => {
      p.forecast?.forEach(f => {
        if (f.netOrder > 0 && f.isSeason) {
          items.push({
            sku: p.sku,
            name: p.name,
            qty: f.netOrder,
            unit_price: p.avgPurchasePrice || 0,
            expected_date: f.orderByDate,
            month: f.month,
            year: f.year
          });
        }
      });
    });
    setOrderDialog({ supplier, items });
  };

  const createOrder = async () => {
    if (!orderDialog) return;
    setCreatingOrder(true);
    try {
      const res = await fetch(`${PURCHASE_API}/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': APP_API_KEY },
        body: JSON.stringify({
          supplier_id: orderDialog.supplier.id,
          status: 'draft',
          notes: 'Gegenereerd vanuit prognose',
          items: orderDialog.items.map(i => ({
            sku: i.sku,
            name: i.name,
            quantity_ordered: i.qty,
            unit_price: i.unit_price,
            expected_date: i.expected_date
          }))
        })
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Fout bij aanmaken');
      setOrderSuccess(result.po_number || 'Aangemaakt');
      setOrderDialog(null);
    } catch(e) {
      alert('Fout: ' + e.message);
    } finally {
      setCreatingOrder(false);
    }
  };

  const load = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const url = forceRefresh ? `${API}?refresh=1` : API;
      const res = await fetch(url, { headers: { 'x-api-key': APP_API_KEY } });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setData(json);
      setLastLoaded(new Date());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <CircularProgress sx={{ color: '#ffcc00' }} />
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        Verkoopdata ophalen uit WooCommerce, Shopify en Bol.com...
      </Typography>
    </Box>
  );

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;

  const currentMonth = new Date().getMonth();
  const forecastMonths = data.products?.[0]?.forecast || [];

  // Filter producten
  const filtered = (data.products || []).filter(p => {
    if (filterAbc !== 'all' && p.abcClass !== filterAbc) return false;
    if (searchText) {
      const q = searchText.toLowerCase();
      return p.sku.toLowerCase().includes(q) || p.name.toLowerCase().includes(q);
    }
    return true;
  });

  // Totaal inkoopadvies komende 4 maanden
  const totalForecast = forecastMonths.map((_, i) =>
    filtered.reduce((s, p) => s + (p.forecast?.[i]?.netOrder || 0), 0)
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" fontWeight="bold">Inkoopprognose</Typography>
          <TextField
            size="small"
            placeholder="Zoek SKU of product..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            sx={{ width: 220 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment>,
              endAdornment: searchText ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchText('')}><CloseIcon sx={{ fontSize: 14 }} /></IconButton>
                </InputAdornment>
              ) : null
            }}
          />
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select value={filterAbc} onChange={e => setFilterAbc(e.target.value)}>
              <MenuItem value="all">Alle</MenuItem>
              {['A','B','C','D'].map(c => (
                <MenuItem key={c} value={c}>
                  <Chip label={c} size="small" sx={{
                    backgroundColor: ABC_COLORS[c].bg, color: ABC_COLORS[c].text,
                    border: `1px solid ${ABC_COLORS[c].border}`, fontWeight: 'bold', height: 18, fontSize: 11
                  }} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {lastLoaded && (
            <Typography variant="caption" color="text.secondary">
              {data.channels?.wc || data.wcOrders || 0} WC + {data.channels?.shopify || data.shopifyOrders || 0} Shopify + {data.channels?.bol || 0} Bol.com orders
            </Typography>
          )}
          <Button startIcon={<RefreshIcon />} onClick={() => load(true)} size="small" disabled={loading}>
            Vernieuwen
          </Button>
        </Box>
      </Box>

      {/* Info */}
      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
        Prognose op basis van WooCommerce + Shopify + Bol.com verkoophistorie 2024–2025. Netto besteladvies houdt rekening met huidige voorraad (alle magazijnen) en open inkooporders.
        Groei wordt automatisch berekend. IJzeren voorraad = 95% servicegraad (1.65 × σ × √(levertijd/4.33)).
        Seizoen: maart t/m augustus, forecast voor komende 4 maanden. IJzeren voorraad = 95% servicegraad.
      </Alert>

      {/* ABC samenvatting */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        {['A','B','C','D'].map(c => {
          const count = (data.products || []).filter(p => p.abcClass === c).length;
          const abc = ABC_COLORS[c];
          return (
            <Paper key={c} elevation={0} onClick={() => setFilterAbc(filterAbc === c ? 'all' : c)}
              sx={{ p: 1.5, border: `1px solid ${abc.border}`, backgroundColor: abc.bg,
                borderRadius: 2, cursor: 'pointer', minWidth: 100, textAlign: 'center',
                opacity: filterAbc !== 'all' && filterAbc !== c ? 0.4 : 1 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ color: abc.text }}>{count}</Typography>
              <Typography variant="caption" sx={{ color: abc.text }}>{c}-artikelen</Typography>
            </Paper>
          );
        })}
      </Box>

      {/* Besteladvies per leverancier */}
      {Object.keys(productsBySupplier).length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            Besteladvies per leverancier
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {Object.entries(productsBySupplier).map(([suppId, prods]) => {
              const supplier = supplierMap[suppId];
              if (!supplier) return null;
              const totalItems = prods.reduce((s, p) =>
                s + p.forecast.filter(f => f.netOrder > 0).length, 0);
              const totalQty = prods.reduce((s, p) =>
                s + p.forecast.reduce((s2, f) => s2 + (f.netOrder || 0), 0), 0);
              const hasUrgent = prods.some(p => p.forecast.some(f => f.urgent && f.netOrder > 0));
              return (
                <Paper key={suppId} elevation={0} sx={{
                  p: 2, border: `1px solid ${hasUrgent ? '#f44336' : '#e0e0e0'}`,
                  borderRadius: 2, minWidth: 220,
                  backgroundColor: hasUrgent ? '#fff5f5' : '#fafafa'
                }}>
                  <Typography variant="body2" fontWeight="bold">{supplier.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {prods.length} producten · {totalItems} bestelregels · {totalQty} stuks totaal
                  </Typography>
                  {hasUrgent && (
                    <Chip label="⚠ Urgent" size="small" color="error" sx={{ display: 'block', mt: 0.5, width: 'fit-content' }} />
                  )}
                  <Button
                    variant="contained" size="small" startIcon={<OrderIcon />}
                    onClick={() => openOrderDialog(parseInt(suppId))}
                    sx={{ mt: 1, backgroundColor: '#ffcc00', color: '#000',
                      '&:hover': { backgroundColor: '#e6b800' }, display: 'block' }}
                  >
                    Inkooporder aanmaken
                  </Button>
                </Paper>
              );
            })}
          </Box>
          {Object.keys(productsBySupplier).length === 0 && (
            <Alert severity="info">Koppel eerst SKUs aan leveranciers via Inkoop → SKU Koppeling.</Alert>
          )}
        </Box>
      )}

      {Object.keys(productsBySupplier).length === 0 && suppliers.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Koppel SKUs aan leveranciers via <strong>Inkoop → SKU Koppeling</strong> om besteladvies per leverancier te zien.
        </Alert>
      )}

      {/* Succes melding */}
      {orderSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOrderSuccess(null)}>
          Inkooporder <strong>{orderSuccess}</strong> aangemaakt als concept. Ga naar Inkooporders om te bewerken en versturen.
        </Alert>
      )}

      {/* Inkooporder dialog */}
      <Dialog open={!!orderDialog} onClose={() => setOrderDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Inkooporder aanmaken — {orderDialog?.supplier?.name}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Onderstaande regels komen uit het besteladvies. Je kunt de aantallen en datums aanpassen in de Inkooporders tab na aanmaken.
          </Alert>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>SKU</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Product</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Maand</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Aantal</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Gewenste levering</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Prijs/stuk</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orderDialog?.items.map((item, i) => (
                <TableRow key={i}>
                  <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{item.sku}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{item.name}</Typography></TableCell>
                  <TableCell align="center"><Chip label={`${item.month} ${item.year}`} size="small" /></TableCell>
                  <TableCell align="right"><Typography variant="body2" fontWeight="bold">{item.qty}</Typography></TableCell>
                  <TableCell align="center"><Typography variant="body2">{new Date(item.expected_date).toLocaleDateString('nl-NL')}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="body2">€ {parseFloat(item.unit_price || 0).toFixed(2)}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Totaal: {orderDialog?.items.reduce((s, i) => s + i.qty, 0)} stuks ·{' '}
            € {orderDialog?.items.reduce((s, i) => s + (i.qty * (i.unit_price || 0)), 0).toFixed(2)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrderDialog(null)}>Annuleren</Button>
          <Button variant="contained" onClick={createOrder} disabled={creatingOrder}
            sx={{ backgroundColor: '#ffcc00', color: '#000', '&:hover': { backgroundColor: '#e6b800' } }}>
            {creatingOrder ? <CircularProgress size={18} /> : 'Inkooporder aanmaken'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hoofdtabel */}
      <TableContainer component={Paper} elevation={0}
        sx={{ border: '1px solid #e0e0e0', borderRadius: 2, maxHeight: 'calc(100vh - 300px)', overflow: 'auto', overflowX: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead sx={{ '& th': { backgroundColor: '#f5f5f5', borderBottom: '2px solid #e0e0e0' } }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 220 }}>Product</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 120 }}>Seizoen</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                <Tooltip title="Totaal verkocht 2024+2025"><span>Verkopen</span></Tooltip>
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Groei</TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 160, backgroundColor: '#fafafa', borderLeft: '2px solid #e0e0e0' }}>
                Situatie nu
              </TableCell>
              {forecastMonths.map((f, i) => (
                <TableCell key={i} align="center" sx={{
                  fontWeight: 'bold', minWidth: 100,
                  backgroundColor: f.isSeason ? '#f0f7ff' : '#f5f5f5',
                  color: f.isSeason ? '#1565c0' : 'inherit'
                }}>
                  <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 'bold' }}>{f.month} {f.year}</Typography><br />
                  <Typography variant="caption" sx={{ fontSize: 9, color: 'text.secondary' }}>
                    {f.isSeason ? 'netto bestellen' : 'buiten seizoen'}
                  </Typography>
                </TableCell>
              ))}
              <TableCell sx={{ width: 40 }} />
            </TableRow>
            {/* Totaalrij */}
            <TableRow sx={{ backgroundColor: '#fffde7' }}>
              <TableCell sx={{ fontWeight: 'bold', fontSize: 12 }}>
                Totaal inkoopadvies ({filtered.length} producten)
              </TableCell>
              <TableCell colSpan={6} />
              {totalForecast.map((t, i) => (
                <TableCell key={i} align="center" sx={{
                  fontWeight: 'bold', fontSize: 12,
                  color: forecastMonths[i]?.isSeason ? 'success.dark' : 'text.secondary'
                }}>
                  {t > 0 ? t : '—'}
                </TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={20} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Geen producten gevonden
                </TableCell>
              </TableRow>
            ) : filtered.map(product => (
              <ProductRow
                key={product.sku}
                product={product}
                expanded={expandedSku === product.sku}
                onToggle={() => setExpandedSku(expandedSku === product.sku ? null : product.sku)}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Alert severity="warning" sx={{ mt: 2 }} icon={<InfoIcon />}>
        <strong>Let op:</strong> dit is een prognose op basis van historische data uit WooCommerce, Shopify en Bol.com (2024+).
        Controleer altijd met de actuele voorraad in de Bestellen-tab voordat je een inkooporder aanmaakt.
      </Alert>
    </Box>
  );
}
